import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const AutoBodyPainting = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my auto body painting business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps auto body painting businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for auto body painting businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most auto body painting businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my auto body painting business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like auto body painting. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Auto Body Painting - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Auto Body Painting CRM", url: "/industries/auto-body-painting" }
  ];

  const industryData = {
    name: 'Auto Body & Painting',
    icon: '🎨',
    title: 'Stop Missing Accident Repair Enquiries & Win More Insurance Jobs',
    subtitle: 'Capture accident repair requests 24/7, provide instant quote estimates, and automate insurance company communications',
    
    // Problem Section - Challenges they face
    challenges: [
      'Missing accident repair calls because you\'re in the spray booth or prepping vehicles',
      'Spending hours chasing insurance companies for approvals and claim updates',
      'Customers get quotes from 3+ body shops - you have no way to follow up effectively',
      'Insurance-approved repairer status but no pipeline of direct insurance referrals',
      'Quote requests require detailed damage photos but customers struggle to send them',
      'Repair timelines change but customers aren\'t kept informed about delays',
      'Completed beautiful restorations but customers never leave reviews',
      'Courtesy car availability confusion causes booking friction'
    ],
    
    // Solution Section - How we solve these
    solutions: [
      'Accident damage request form with photo upload captures enquiries 24/7 - customers share damage images and incident details instantly',
      'Instant quote estimate calculator provides ballpark pricing for common repairs (bumper, door, panel, full respray) before detailed assessment',
      'Insurance claim management portal tracks claim numbers, approvals, and insurance company communications in one place',
      'Automated quote follow-up sequences re-engage customers who requested quotes but didn\'t book',
      'Job progress update automation sends SMS notifications at key stages: vehicle received → assessment complete → parts ordered → painting started → ready for collection',
      'Courtesy car availability calendar lets customers check and book courtesy vehicles online',
      'Review automation with before/after photo requests builds your reputation and dominates local accident repair search results'
    ],
    
    // Features Section - What's included in snapshot
    features: [
      '24/7 Accident Damage Request Form with Photo Upload',
      'Instant Repair Quote Estimate Calculator',
      'Before/After Restoration Photo Gallery & Showcase',
      'Insurance Claim Tracking & Management Portal',
      'Automated Insurance Company Communication Templates',
      'Quote Follow-Up Sequences (for non-converted enquiries)',
      'Job Progress Update Automation (SMS & Email)',
      'Courtesy Car Booking Calendar & Availability Tracker',
      'Parts Arrival & Repair Delay Notifications',
      'Google Review Request Automation with Photo Uploads',
      'Insurance Approved Repairer Badge & Verification',
      'Customer Portal (track repair status & photos)',
      'Multi-Location Workshop Management (if applicable)',
      'Paint Code Database & Color Match Gallery'
    ],
    
    // Results Section - Success metrics
    results: [
      '73% quote conversion rate improvement',
      '5.2x more insurance referral jobs',
      '24/7 accident repair enquiry capture'
    ],
    
    // SEO Section
    metaTitle: 'Auto Body Shop Growth Platform | Win More Accident Repair & Insurance Jobs',
    metaDescription: 'Capture accident repairs 24/7 with photo upload, instant quotes, insurance claim management, and automated follow-up for body shops.',
    
    // Reviews Section
    reviews: [
      {
        author: 'Daniel F.',
        business: 'Accident Repair Centre',
        location: 'Birmingham',
        rating: 5,
        text: 'The photo upload damage form is absolutely brilliant. Customers share accident damage photos and insurance details 24/7, even when we\'re closed. We can now provide rough quote estimates before they even visit. Quote conversion went from 34% to 71% in just 4 months. Capturing way more insurance-approved work too.',
        verified: true
      },
      {
        author: 'Lisa K.',
        business: 'Body Shop & Paint Specialist',
        location: 'London',
        rating: 5,
        text: 'The insurance claim tracking portal is a lifesaver. We track every claim number, approval status, and insurance company contact in one place. No more frantic searching through emails. The automated insurance communication templates save us hours every week. We\'re now the preferred repairer for 2 major insurance companies.',
        verified: true
      },
      {
        author: 'Simon R.',
        business: 'Collision Repair & Refinishing',
        location: 'Manchester',
        rating: 5,
        text: 'The automated job progress updates are brilliant. Customers get SMS notifications when their vehicle is received, assessed, in paint, and ready for collection. No more "when will my car be ready?" calls 10 times a day. Our customer satisfaction has gone through the roof. Google reviews increased from 9 to 58 in 6 months.',
        verified: true
      },
      {
        author: 'Karen H.',
        business: 'Auto Body Repair & Restoration',
        location: 'Liverpool',
        rating: 5,
        text: 'The quote follow-up sequences re-engage customers who asked for quotes but didn\'t book. We recover about 30% of lost quotes now with the automated follow-up emails and SMS. The before/after photo gallery also helps massively - customers see the quality of our work before committing. Best investment we\'ve made.',
        verified: true
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Auto Body Painting UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for auto body painting businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for auto body painting UK, auto body painting business software, auto body painting customer management, auto body painting lead generation UK"
        url="/industries/auto-body-painting"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default AutoBodyPainting;